import api from './api.js';

/**
 * Admin Step-Up MFA Service
 * API wrappers for the TOTP second factor that gates every administrative
 * route (CASA 4.3.1). Endpoints: api/src/modules/admin/routes.js.
 *
 * These five are gated on the ADMIN_EMAILS allowlist ONLY, not on a live
 * elevation — demanding an elevation in order to obtain one is unsatisfiable.
 * The one exception is re-enrollment: enrolling over an existing authenticator
 * answers 403 ADMIN_MFA_REQUIRED until the session is elevated.
 *
 * The pure helpers (error copy, code normalization, the interceptor handoff)
 * live in adminMfaGate.js so the interceptor can import them without a cycle.
 */

export const getAdminMfaStatus = async () => {
  const response = await api.get('/admin/mfa/status');
  return response.data;
};

// Mints a candidate secret. Nothing is persisted until a code confirms it, so
// an abandoned enrollment leaves the account exactly as it was.
export const beginAdminMfaEnrollment = async () => {
  const response = await api.post('/admin/mfa/enroll');
  return response.data;
};

export const confirmAdminMfaEnrollment = async (code) => {
  const response = await api.post('/admin/mfa/enroll/confirm', { code });
  return response.data;
};

// Exchanges a current code for a 15-minute elevation on this session.
export const challengeAdminMfa = async (code) => {
  const response = await api.post('/admin/mfa/challenge', { code });
  return response.data;
};

/**
 * Drop this session's elevation early. Resolves `{ elevated: false }` only when
 * the revoke actually landed; a failed revoke REJECTS with 503
 * ADMIN_MFA_STEP_DOWN_FAILED and the session stays elevated until its TTL runs
 * out. Callers must not treat a rejection as "stepped down" — render
 * describeAdminMfaError(err), which says the elevation expires on its own.
 */
export const stepDownAdminMfa = async () => {
  const response = await api.post('/admin/mfa/step-down');
  return response.data;
};
